import { Html5Qrcode } from "html5-qrcode";
import { forwardRef, useEffect, useId, useImperativeHandle, useRef } from "react";

export type ScannerHtml5QrcodeProps = {
	onResult: (result: { data: string }) => void;
	className?: string;
};

export type ScannerHtml5QrcodeRef = {
	switchCamera: () => Promise<void>;
	hasMultipleCameras: () => Promise<boolean>;
	toggleTorch: () => Promise<void>;
};

export const ScannerHtml5Qrcode = forwardRef<ScannerHtml5QrcodeRef, ScannerHtml5QrcodeProps>((props, forwardedRef) => {
	const { onResult, className } = props;
	const onResultRef = useRef(onResult);
	const scannerRef = useRef<Html5Qrcode | null>(null);
	const currentFacingModeRef = useRef<"environment" | "user">("environment");
	const torchEnabledRef = useRef(false);
	const containerId = useId().replace(/:/g, "_");

	onResultRef.current = onResult;

	useImperativeHandle(forwardedRef, () => ({
		switchCamera: async () => {
			const scanner = scannerRef.current;
			if (!scanner) return;

			const isScanning = scanner.isScanning;
			if (isScanning) {
				await scanner.stop();
			}

			const newFacingMode = currentFacingModeRef.current === "environment" ? "user" : "environment";
			currentFacingModeRef.current = newFacingMode;
			torchEnabledRef.current = false;

			await scanner.start(
				{ facingMode: newFacingMode },
				{
					fps: 10,
					qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
						const minDimension = Math.min(viewfinderWidth, viewfinderHeight);
						const size = Math.floor(minDimension * 0.7);
						return { width: size, height: size };
					},
					aspectRatio: 1.0,
				},
				(decodedText) => {
					onResultRef.current({ data: decodedText });
				},
				() => {}
			);
		},
		hasMultipleCameras: async () => {
			const cameras = await Html5Qrcode.getCameras();
			return cameras.length > 1;
		},
		toggleTorch: async () => {
			const scanner = scannerRef.current;
			if (!scanner) return;

			const newTorchState = !torchEnabledRef.current;
			await scanner.applyVideoConstraints({
				// @ts-expect-error - torch is a valid constraint but not in types
				advanced: [{ torch: newTorchState }],
			});
			torchEnabledRef.current = newTorchState;
		},
	}));

	useEffect(() => {
		const scanner = new Html5Qrcode(containerId);
		scannerRef.current = scanner;

		scanner.start(
			{ facingMode: "environment" },
			{
				fps: 10,
				qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
					const minDimension = Math.min(viewfinderWidth, viewfinderHeight);
					const size = Math.floor(minDimension * 0.7);
					return { width: size, height: size };
				},
				aspectRatio: 1.0,
			},
			(decodedText) => {
				onResultRef.current({ data: decodedText });
			},
			() => {}
		);

		return () => {
			if (scanner.isScanning) {
				scanner.stop().catch(() => {});
			}
		};
	}, [containerId]);

	return <div id={containerId} className={className} />;
});
